import { useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Camera, FileText, Image as ImageIcon, Info, Plus, Upload, X } from "lucide-react";
import SectionCard, { inputCls, labelCls } from "./SectionCard";
import { api, photoUrl, formatApiError } from "../../api";
import { belgeTarih, belgeDurumu, fmtDateShort, BELGE_EXTRAS, belgeEk } from "../../lib/labels";

const DATE_BELGELER = [
  { key: "kasko", label: "Kasko" },
  { key: "sigorta", label: "Trafik Sigortası" },
  { key: "muayene", label: "Muayene (TÜVTÜRK)" },
];

// Gizli dosya input'lari agacta tek ve sabit bir yerde render edilir (bkz. return).
// DocUpload/QuickUpload bilesen govdesi icinde tanimli oldugu icin her render'da
// yeniden olusuyorlar; input'lar onlarin icinde dururken, dosya secicisini acan
// input DOM'dan kopuyor ve secim sonrasi change olayi React'e hic ulasmiyordu.
const ALL_BELGE_KEYS = [...DATE_BELGELER.map((b) => b.key), ...BELGE_EXTRAS.map((b) => b.key)];

// Bir belge turu birden fazla dosya tutar: dosyalar = hepsi, dosya = ilki (eski okuyucular icin).
const MAX_FILES = 10;
const filesOf = (e) => (e && Array.isArray(e.dosyalar) ? e.dosyalar.filter(Boolean) : (e && e.dosya ? [e.dosya] : []));
const withFiles = (e, files) => ({ ...e, dosyalar: files, dosya: files[0] || "" });
const isPdf = (p) => String(p).toLowerCase().endsWith(".pdf");

function FileChips({ files, onRemove, tid, disabled }) {
  if (!files.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`${tid}-files`}>
      {files.map((f, i) => (
        <span key={f} className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-slate-900/60 pl-2 pr-1 py-1">
          <a data-testid={`${tid}-file-${i}`} href={photoUrl(f)} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200">
            {isPdf(f)
              ? <><FileText className="w-3.5 h-3.5" /> PDF {i + 1}</>
              : <img src={photoUrl(f)} alt={`belge ${i + 1}`} className="w-10 h-7 object-cover rounded border border-white/10" />}
          </a>
          {onRemove && (
            <button type="button" data-testid={`${tid}-remove-${i}`} aria-label="Dosyayı kaldır" disabled={disabled}
              onClick={() => onRemove(f)}
              className="p-1 rounded text-slate-400 hover:text-red-300 hover:bg-white/5 disabled:opacity-50">
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

export default function BelgelerSection({ vehicle, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [belgeler, setBelgeler] = useState(null);
  const [uploading, setUploading] = useState("");
  const [uploadingView, setUploadingView] = useState("");
  const [pickerFor, setPickerFor] = useState("");
  const fileRefs = useRef({});
  const viewFileRefs = useRef({});

  const startEdit = () => {
    const dates = belgeTarih(vehicle.belgeler);
    const extras = {};
    for (const b of BELGE_EXTRAS) extras[b.key] = belgeEk(vehicle.belgeler, b.key);
    setBelgeler({ ...dates, ...extras });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/section/belgeler`, { value: belgeler });
      onSaved(data);
      setEditing(false);
      toast.success("Belgeler kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (key, e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setBelgeler((s) => ({ ...s, [key]: withFiles(s[key], [...filesOf(s[key]), data.path]) }));
      toast.success("Belge görseli yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading("");
  };

  const removeDoc = (key, path) =>
    setBelgeler((s) => ({ ...s, [key]: withFiles(s[key], filesOf(s[key]).filter((f) => f !== path)) }));

  const DocUpload = ({ k }) => (
    <div className="mt-3">
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`belge-upload-${k}`} onClick={() => fileRefs.current[k]?.click()} disabled={!!uploading || filesOf(belgeler[k]).length >= MAX_FILES}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-white/20 text-slate-300 text-xs hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {uploading === k ? "Yükleniyor..." : filesOf(belgeler[k]).length ? "Başka Belge Ekle" : "Belge Ekle (PDF veya görsel, opsiyonel)"}
        </button>
      </div>
      <FileChips files={filesOf(belgeler[k])} tid={`belge-edit-${k}`} onRemove={(f) => removeDoc(k, f)} />
    </div>
  );

  const pickSource = (k, mode) => {
    const el = viewFileRefs.current[k];
    if (!el) return;
    if (mode === "pdf") { el.accept = "application/pdf"; el.removeAttribute("capture"); }
    else {
      el.accept = "image/jpeg,image/png,image/webp";
      if (mode === "camera") el.setAttribute("capture", "environment"); else el.removeAttribute("capture");
    }
    setPickerFor("");
    el.click();
  };

  // Goruntuleme modundan dogrudan belge yukleme: yukle + birlestir + aninda kaydet
  const quickUpload = async (key, e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setUploadingView(key);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      const merged = { ...belgeTarih(vehicle.belgeler) };
      for (const b of BELGE_EXTRAS) merged[b.key] = belgeEk(vehicle.belgeler, b.key);
      merged[key] = withFiles(merged[key], [...filesOf(merged[key]), data.path]);
      const res = await api.put(`/my-vehicles/${vehicle.id}/section/belgeler`, { value: merged });
      onSaved(res.data);
      toast.success("Belge yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploadingView("");
  };

  const removeQuick = async (key, path) => {
    setUploadingView(key);
    try {
      const merged = { ...belgeTarih(vehicle.belgeler) };
      for (const b of BELGE_EXTRAS) merged[b.key] = belgeEk(vehicle.belgeler, b.key);
      merged[key] = withFiles(merged[key], filesOf(merged[key]).filter((f) => f !== path));
      const res = await api.put(`/my-vehicles/${vehicle.id}/section/belgeler`, { value: merged });
      onSaved(res.data);
      toast.success("Belge kaldırıldı");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploadingView("");
  };

  const QuickUpload = ({ k }) => (
    <div className="mt-2.5">
      {pickerFor === k ? (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-testid={`belge-quick-camera-${k}`} onClick={() => pickSource(k, "camera")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
            <Camera className="w-3.5 h-3.5" /> Kamera
          </button>
          <button type="button" data-testid={`belge-quick-gallery-${k}`} onClick={() => pickSource(k, "gallery")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
            <ImageIcon className="w-3.5 h-3.5" /> Galeri
          </button>
          <button type="button" data-testid={`belge-quick-pdf-${k}`} onClick={() => pickSource(k, "pdf")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
            <FileText className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      ) : (
        <button type="button" data-testid={`belge-quick-upload-${k}`} onClick={() => setPickerFor(k)} disabled={!!uploadingView}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 text-xs font-semibold hover:bg-cyan-400/15 transition-colors disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> {uploadingView === k ? "Yükleniyor..." : "Belge Yükle"}
        </button>
      )}
    </div>
  );

  const DocThumb = ({ entry, k }) => (
    <FileChips files={filesOf(entry)} tid={`belge-view-${k}`} onRemove={(f) => removeQuick(k, f)} disabled={!!uploadingView} />
  );

  const current = belgeTarih(vehicle.belgeler);

  return (
    <SectionCard testid="section-belgeler" title="Belgeler" Icon={CalendarClock}
      desc="Tüm araç belgelerinizi tek merkezden yönetin. Belge görseli yüklemek zorunlu değildir; bilgileri elle de girebilirsiniz. Sigorta/kasko/muayene bitimine 30 gün, 15 gün kala ve son gün bildirim alırsınız."
      editing={editing} onEdit={startEdit} onCancel={() => setEditing(false)} onSave={save} saving={saving}>
      {ALL_BELGE_KEYS.map((k) => (
        <input key={`quick-${k}`} ref={(el) => { viewFileRefs.current[k] = el; }} type="file" className="hidden"
          data-testid={`belge-quick-file-${k}`} onChange={(e) => quickUpload(k, e)} />
      ))}
      {ALL_BELGE_KEYS.map((k) => (
        <input key={`doc-${k}`} ref={(el) => { fileRefs.current[k] = el; }} type="file" className="hidden"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf"
          data-testid={`belge-file-${k}`} onChange={(e) => uploadDoc(k, e)} />
      ))}
      {!editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {DATE_BELGELER.map((b) => {
              const t = current[b.key];
              const st = belgeDurumu(t.bitis);
              return (
                <div key={b.key} data-testid={`belge-view-${b.key}`}
                  className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-100">{b.label}</span>
                    <span data-testid={`belge-badge-${b.key}`} className={`text-[10px] font-bold px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="mt-1.5 text-xs text-slate-400">Başlangıç: <span className="font-semibold text-slate-200">{fmtDateShort(t.baslangic)}</span></p>
                  <p className="text-xs text-slate-400">Bitiş: <span className="font-semibold text-slate-200">{fmtDateShort(t.bitis)}</span>{st.days !== null && st.days >= 0 && <span className="text-slate-500"> ({st.days} gün kaldı)</span>}</p>
                  <DocThumb entry={t} k={b.key} />
                  <QuickUpload k={b.key} />
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BELGE_EXTRAS.map((b) => {
              const t = belgeEk(vehicle.belgeler, b.key);
              const has = t.tarih || t.belge_no || t.not || filesOf(t).length;
              return (
                <div key={b.key} data-testid={`belge-view-${b.key}`}
                  className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="text-sm font-semibold text-slate-100">{b.label}</span>
                  </div>
                  {has ? (
                    <div className="mt-1.5 space-y-0.5">
                      {t.tarih && <p className="text-xs text-slate-400">Tarih: <span className="font-semibold text-slate-200">{fmtDateShort(t.tarih)}</span></p>}
                      {t.belge_no && <p className="text-xs text-slate-400">Belge No: <span className="font-semibold text-slate-200">{t.belge_no}</span></p>}
                      {t.not && <p className="text-xs text-slate-400">{t.not}</p>}
                      <DocThumb entry={t} k={b.key} />
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs text-slate-500">Belge eklenmedi</p>
                  )}
                  <QuickUpload k={b.key} />
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-400" />
            Tarihler yaklaştığında bildirim zilinize uyarı düşer (30 gün, 15 gün kala ve son gün). Tarihi güncellerseniz uyarılar yeni tarihe göre yeniden planlanır.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {DATE_BELGELER.map((b) => (
            <div key={b.key} className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-sm font-semibold text-slate-200 mb-3">{b.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Başlangıç Tarihi</label>
                  <input data-testid={`belge-edit-${b.key}-baslangic`} type="date" value={belgeler[b.key].baslangic || ""}
                    onChange={(e) => setBelgeler((s) => ({ ...s, [b.key]: { ...s[b.key], baslangic: e.target.value } }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Bitiş Tarihi</label>
                  <input data-testid={`belge-edit-${b.key}-bitis`} type="date" value={belgeler[b.key].bitis || ""}
                    onChange={(e) => setBelgeler((s) => ({ ...s, [b.key]: { ...s[b.key], bitis: e.target.value } }))} className={inputCls} />
                </div>
              </div>
              <DocUpload k={b.key} />
            </div>
          ))}
          {BELGE_EXTRAS.map((b) => (
            <div key={b.key} className="rounded-xl border border-white/10 bg-slate-950/40 p-4">
              <p className="text-sm font-semibold text-slate-200 mb-3">{b.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tarih (opsiyonel)</label>
                  <input data-testid={`belge-edit-${b.key}-tarih`} type="date" value={belgeler[b.key].tarih || ""}
                    onChange={(e) => setBelgeler((s) => ({ ...s, [b.key]: { ...s[b.key], tarih: e.target.value } }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Belge No (opsiyonel)</label>
                  <input data-testid={`belge-edit-${b.key}-no`} value={belgeler[b.key].belge_no || ""}
                    onChange={(e) => setBelgeler((s) => ({ ...s, [b.key]: { ...s[b.key], belge_no: e.target.value } }))} className={inputCls} />
                </div>
              </div>
              <div className="mt-3">
                <label className={labelCls}>Not (opsiyonel)</label>
                <input data-testid={`belge-edit-${b.key}-not`} value={belgeler[b.key].not || ""}
                  onChange={(e) => setBelgeler((s) => ({ ...s, [b.key]: { ...s[b.key], not: e.target.value } }))} className={inputCls} />
              </div>
              <DocUpload k={b.key} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
